/**
 * Group fan-out — SPEC §13.1 (pairwise, ≤32 members) over protocol v2.
 * Every member copy is an independent v2 session message, so recipients learn
 * the AUTHENTICATED sender of each copy (v1 trusted a spoofable wire field).
 * One stable message id is shared by all copies so replies/reactions line up.
 */
import { toHex } from "@veto/crypto-bridge";
import type { Contact, GroupRecord, PublishOutcome } from "@veto/domain";
import {
  CONTENT_TYPE_GROUP,
  canonicalGroupKeyBytes,
  encodeGroupPayload,
  encryptGroupText,
  generateGroupSenderKey,
  toWireGroupMembers,
} from "@veto/protocol";
import type { Messenger } from "./messenger";

/** Member contacts we can reach (members without a local contact are skipped). */
export function reachableMembers(group: GroupRecord, contacts: Contact[], selfHex: string): Contact[] {
  const out: Contact[] = [];
  for (const m of group.members) {
    const key = m.identityKeyHex.toLowerCase();
    if (key === selfHex.toLowerCase()) continue;
    const c = contacts.find((x) => x.messagingPublicKeyHex.toLowerCase() === key);
    if (c && !c.blockedAt) out.push(c);
  }
  return out;
}

/** Admin-signed epoch key distribution (FR-GRP-001/002, SEC-1-6). */
export async function fanoutGroupKey(opts: {
  group: GroupRecord;
  contacts: Contact[];
  messenger: Messenger;
}): Promise<PublishOutcome> {
  const { group, messenger } = opts;
  const adminIdentityKeyHex = messenger.account.messagingPublicKeyHex.toLowerCase();
  const wireMembers = toWireGroupMembers(group.members);
  const canon = canonicalGroupKeyBytes({
    groupId: group.groupId,
    epoch: group.epoch,
    title: group.title,
    members: wireMembers,
    senderKeyB64: group.senderKeyB64,
    adminIdentityKeyHex,
  });
  const adminSigHex = toHex(messenger.signAsIdentity(canon));
  const body = encodeGroupPayload({
    op: "group-key",
    groupId: group.groupId,
    epoch: group.epoch,
    title: group.title,
    members: wireMembers,
    senderKeyB64: group.senderKeyB64,
    adminIdentityKeyHex,
    adminSigHex,
  });
  return messenger.send({
    conversationId: group.conversationId,
    recipients: reachableMembers(group, opts.contacts, adminIdentityKeyHex),
    contentType: CONTENT_TYPE_GROUP,
    body,
  });
}

/** Group message: epoch-key AEAD inside each member's pairwise v2 session. */
export async function fanoutGroupMessage(opts: {
  group: GroupRecord;
  plaintext: string;
  contacts: Contact[];
  messenger: Messenger;
  eventId: string;
  createdAt: number;
  replyToEventId?: string;
}): Promise<PublishOutcome> {
  const { group, messenger } = opts;
  const ctB64 = await encryptGroupText(group.senderKeyB64, opts.plaintext, `${group.groupId}:${group.epoch}`);
  const body = encodeGroupPayload({
    op: "group-msg",
    groupId: group.groupId,
    epoch: group.epoch,
    ctB64,
    replyToEventId: opts.replyToEventId,
  });
  return messenger.send({
    conversationId: group.conversationId,
    recipients: reachableMembers(group, opts.contacts, messenger.account.messagingPublicKeyHex),
    contentType: CONTENT_TYPE_GROUP,
    body,
    eventId: opts.eventId,
    createdAt: opts.createdAt,
    tracksMessage: true,
  });
}

export { generateGroupSenderKey };
